import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import XIcon from "@redotech/redo-icons/x_filled.svg";
import { RedoOutboundLabelsRpcClientContext } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import { WorkOrderWorkStepFlowStep } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { StepType } from "@redotech/redo-model/advanced-flow/steps";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import * as inspectorStyles from "@redotech/redo-web/inspector.module.css";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import { memo, useMemo, useState } from "react";
import { useAvailableSteps } from "../hooks/use-available-steps";
import type {
  FrontendStepType,
  PartialFlowStep,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../index";

const NONE_STATION_VALUE = "__none__";

const WorkOrderWorkStepInspector = memo(function WorkOrderWorkStepInspector({
  step,
  setStep,
  onClose,
  steps,
  setHoveredNodeId,
}: StepTypeDetailsProps<PartialWithRequiredType<WorkOrderWorkStepFlowStep>>) {
  const displayAttributes = step.displayAttributes ?? [];
  const [displayAttrModalOpen, setDisplayAttrModalOpen] = useState(false);

  const nextStep = step.nextId
    ? (steps as PartialFlowStep[] | undefined)?.find(
        (s) => s.id === step.nextId,
      )
    : undefined;

  const { availableSteps, createConnectionHandler, onItemHover } =
    useAvailableSteps(step, steps ?? [], setStep, setHoveredNodeId, "End");

  const handleNextStepSelected = createConnectionHandler(0);

  const { data: stationsData } = useRpcQuery(
    RedoOutboundLabelsRpcClientContext,
    { method: "getWorkOrderStations" as const, args: {} },
  );

  const stationOptions = useMemo(() => {
    const options: { id: string; type: "text"; text: string; value: string }[] =
      [
        {
          id: NONE_STATION_VALUE,
          type: "text",
          text: "None",
          value: NONE_STATION_VALUE,
        },
      ];
    for (const station of stationsData?.stations ?? []) {
      options.push({
        id: station.id,
        type: "text",
        text: station.name,
        value: station.id,
      });
    }
    return options;
  }, [stationsData]);

  function addDisplayAttribute() {
    setStep({
      ...step,
      displayAttributes: [
        ...displayAttributes,
        { label: "", attributeKey: "" },
      ],
    });
  }

  function updateDisplayAttribute(
    index: number,
    field: "label" | "attributeKey",
    value: string,
  ) {
    setStep({
      ...step,
      displayAttributes: displayAttributes.map((attr, i) =>
        i === index ? { ...attr, [field]: value } : attr,
      ),
    });
  }

  function removeDisplayAttribute(index: number) {
    setStep({
      ...step,
      displayAttributes: displayAttributes.filter((_, i) => i !== index),
    });
  }

  return (
    <Flex dir="column" gap="xl" justify="flex-start">
      <Flex justify="space-between" w="full">
        <Text fontSize="lg" fontWeight="semibold">
          Work step
        </Text>
        {onClose && (
          <div className={inspectorStyles.closeButton} onClick={onClose}>
            <XIcon height="16" width="16" />
          </div>
        )}
      </Flex>

      <Flex dir="column" gap="md">
        <RedoTextInput
          label="Title"
          setValue={(value) => setStep({ ...step, title: value })}
          value={step.title ?? ""}
        />
        <RedoTextareaInputField
          autoExpand
          label="Instructions"
          setValue={(value) => setStep({ ...step, instructions: value })}
          style={{ minHeight: "72px" }}
          value={step.instructions ?? ""}
        />
        <RedoSingleSelectDropdownInput<string>
          fitToAnchor
          label="Station"
          options={stationOptions}
          optionSelected={({ item }) =>
            setStep({
              ...step,
              stationId: item.value === NONE_STATION_VALUE ? null : item.value,
            })
          }
          placeholder="Select station"
          selectedItem={
            step.stationId
              ? stationOptions.find((opt) => opt.value === step.stationId)
              : stationOptions[0]
          }
        />
        <RedoCheckbox
          label="Print barcode label after completing this step"
          setValue={(value) =>
            setStep({ ...step, printBarcodeOnComplete: value === true })
          }
          size="sm"
          value={step.printBarcodeOnComplete ?? false}
        />
        <RedoCheckbox
          label="Show sibling batches on completion"
          setValue={(value) =>
            setStep({ ...step, showSiblingBatches: value === true })
          }
          size="sm"
          value={step.showSiblingBatches ?? false}
        />
      </Flex>

      <Flex dir="column" gap="md">
        <Flex justify="space-between" w="full">
          <Text fontSize="md" fontWeight="semibold">
            Display attributes
          </Text>
          <Flex align="center" gap="xs">
            <RedoButton
              hierarchy="tertiary"
              onClick={() => setDisplayAttrModalOpen(true)}
              size="xs"
              text="What is this?"
            />
            <RedoButton
              hierarchy="secondary"
              IconLeading={PlusIcon}
              onClick={addDisplayAttribute}
              size="xs"
              text="Add"
            />
          </Flex>
        </Flex>
        {displayAttributes.map((attr, index) => (
          <Flex align="flex-end" gap="sm" key={index} w="full">
            <RedoTextInput
              fullWidth
              label="Label"
              placeholder="e.g. Engraving"
              setValue={(value) =>
                updateDisplayAttribute(index, "label", value)
              }
              value={attr.label ?? ""}
            />
            <RedoTextInput
              fullWidth
              label="Attribute key"
              placeholder="e.g. engraving"
              setValue={(value) =>
                updateDisplayAttribute(index, "attributeKey", value)
              }
              value={attr.attributeKey ?? ""}
            />
            <RedoButton
              hierarchy="tertiary"
              IconLeading={TrashIcon}
              onClick={() => removeDisplayAttribute(index)}
              size="sm"
              theme="destructive"
            />
          </Flex>
        ))}
      </Flex>

      <Divider />

      <Flex dir="column" gap="md">
        <Text fontSize="md" fontWeight="semibold">
          Next step
        </Text>
        <RedoSingleSelectDropdownInput<string>
          fitToAnchor
          label="After this step completes"
          onOptionMouseEnter={(item) => onItemHover(item.value)}
          onOptionMouseLeave={() => onItemHover(null)}
          options={availableSteps}
          optionSelected={({ item }) => handleNextStepSelected(item.value)}
          placeholder="Select next step"
          selectedItem={
            nextStep
              ? nextStep.type === StepType.DO_NOTHING
                ? availableSteps.find((opt) => opt.value === "unassign")
                : availableSteps.find((opt) => opt.value === nextStep.id)
              : undefined
          }
        />
      </Flex>

      {displayAttrModalOpen && (
        <RedoModal
          hideFooter
          modalSize="sm"
          onModalCloseRequested={() => setDisplayAttrModalOpen(false)}
          title="Display attributes"
        >
          <Flex dir="column" gap="md">
            <Text fontSize="sm">
              Display attributes map line item custom attribute keys to labeled
              fields shown on the work step. This lets workers see relevant
              order customizations without leaving the work order.
            </Text>
            <Text fontSize="sm" fontWeight="semibold">
              How to use
            </Text>
            <Text fontSize="sm">
              Set a <strong>Label</strong> (e.g. &ldquo;Engraving&rdquo;) and
              the matching <strong>Attribute key</strong> (e.g.{" "}
              &ldquo;engraving&rdquo;). When a work order is processed, the
              value of that line item property will be shown under the label you
              defined.
            </Text>
            <Text fontSize="sm" fontWeight="semibold">
              Creating line item properties in Shopify
            </Text>
            <Text fontSize="sm">
              Line item properties are added to products via your storefront
              theme or a Shopify app. The attribute key must exactly match the
              property name on the line item (case-sensitive). Common sources
              include product page customization forms and Shopify app
              integrations that capture personalization data at checkout.
            </Text>
          </Flex>
        </RedoModal>
      )}
    </Flex>
  );
});

export const workOrderWorkStepType: FrontendStepType<
  PartialWithRequiredType<WorkOrderWorkStepFlowStep>
> = {
  valid: (state) => {
    return !!(state.title && state.title.trim().length > 0);
  },
  type: "custom",
  customInspector: WorkOrderWorkStepInspector,
  updateConnection: (state, handleIndex, targetStepId) => {
    return produce(state, (draft) => {
      if (handleIndex === 0) {
        draft.nextId = targetStepId;
      }
    });
  },
  removeConnection: (state, targetStepId) => {
    return produce(state, (draft) => {
      if (draft.nextId === targetStepId) {
        draft.nextId = undefined;
      }
    });
  },
};
